// QUERY PARAM GUARD — allow-list for the app's public (pre-auth) routes.
//
// The unauthenticated surface (landing page, sign-in, password and OAuth
// callback screens) is what gets linked to from outside, so it is where crafted
// URLs land. Each public path declares exactly which query parameters it accepts;
// a request carrying anything else is not "cleaned up and served" — it is treated
// as an address the app does not have, and the 404 page is rendered in place of
// the route. So /?utm_x=… or /login?next=//evil.example resolve to nothing at
// all, and no crafted link can be made to look like a working part of the product.
//
// Routes are matched exactly. Authenticated in-app routes are not listed and are
// left alone — add a path here when it becomes publicly reachable, together with
// the parameters it genuinely needs.

import React from 'react';
import { useLocation } from 'react-router';
import NotFoundPage from '@pages/NotFoundPage';

export const PUBLIC_ROUTE_ALLOWED_PARAMS: Record<string, readonly string[]> = {
  // Marketing landing page: takes no input of any kind.
  '/': [],
  // Read once and then cleared from the address bar by LoginPage itself.
  '/login': ['error', 'passwordChanged'],
  '/forgot-password': [],
  '/change-password': [],
  // Pre-fills the email of an account that has no password yet.
  '/set-password': ['email'],
  // Where the backend lands after M365 sign-in. These have to be accepted or the
  // whole SSO flow 404s; AuthCallbackPage removes them from the address bar as
  // its first action, so the token is never left on screen or in history.
  '/auth/callback': ['token', 'isNewUser', 'error'],
};

const normalisePath = (pathname: string): string => pathname.replace(/\/+$/, '') || '/';

/** True when `search` carries a parameter the route has not declared. */
export const hasDisallowedParams = (search: string, allowed: readonly string[]): boolean => {
  if (!search) return false;
  for (const key of Array.from(new URLSearchParams(search).keys())) {
    if (!allowed.includes(key)) return true;
  }
  return false;
};

/**
 * Renders `children` for a legitimate address, or the 404 page when a public
 * route is called with parameters it does not accept.
 */
const QueryParamGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname, search } = useLocation();

  const allowed = PUBLIC_ROUTE_ALLOWED_PARAMS[normalisePath(pathname)];
  if (allowed && hasDisallowedParams(search, allowed)) {
    return <NotFoundPage />;
  }

  return <>{children}</>;
};

export default QueryParamGuard;
